




import React, { useEffect, useState, useMemo } from "react";
import {
  History,
  Layout,
  ArrowRight,
  Sparkles,
  Calendar,
  Clock,
  Layers,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getUserActivityApi } from "../../app/api";
import TaskActivity from "./TaskActivity";
 
/* ================= HELPERS ================= */
 
const formatSimpleDate = (date) => {
  if (!date) return "";
  const d = new Date(String(date).replace(' ', 'T'));
  return d.toLocaleDateString("en-IN", {
    day: 'numeric',
    month: 'short'
  });
};
 
/* ================= COMPONENT ================= */
 
export default function UserJourney({ userId }) {
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const scrollRef = React.useRef();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  
    const onWheel = (e) => {
      // Only act if horizontal scroll is possible
      if (el.scrollWidth <= el.clientWidth) return;
  
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
  
    el.addEventListener("wheel", onWheel, { passive: false });
  
    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, []);
 
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getUserActivityApi(userId)
      .then((res) => {
        setAllActivities(res?.data || []);
      })
      .catch(() => setAllActivities([]))
      .finally(() => setLoading(false));
  }, [userId]);
 
  // Group activities by Task and find unique tasks
  // const taskGroups = useMemo(() => {
  //   const groups = {};
  //   allActivities.forEach(a => {
  //     if (!groups[a.task_id]) {
  //       groups[a.task_id] = {
  //         id: a.task_id,
  //         title: a.task_title || "Untitled Task",
  //         status: a.task_status,
  //         dueDate: a.task_due_date,
  //         created_by: a.task_created_by,
  //         lastUpdate: a.created_at,
  //         activities: []
  //       };
  //     }
  //     groups[a.task_id].activities.push(a);
  //     if (new Date(a.created_at) > new Date(groups[a.task_id].lastUpdate)) {
  //       groups[a.task_id].lastUpdate = a.created_at;
  //     }
  //   });
 
  //   return Object.values(groups).sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));
  // }, [allActivities]);


  const taskGroups = useMemo(() => {
    const groups = {};
  
    allActivities.forEach(a => {
      if (!groups[a.task_id]) {
        groups[a.task_id] = {
          id: a.task_id,
          title: a.task_title || "Untitled Task",
          status: a.task_status,
          dueDate: a.task_due_date,
          created_by: a.task_created_by,
          lastUpdate: a.created_at,
          activities: [],
  
          // 👉 NEW FIELDS
          startDate: null,
          completedDate: null,
        };
      }
  
      const task = groups[a.task_id];
      task.activities.push(a);
  
      // 👉 LAST UPDATE
      if (new Date(a.created_at) > new Date(task.lastUpdate)) {
        task.lastUpdate = a.created_at;
      }
  
      // 👉 START DATE (first time task appears / TODO)
      // if (a.action === "TASK_STARTED" || a.task_status === "TODO") {
      //   if (!task.startDate) {
      //     task.startDate = a.created_at;
      //   }
      // }

      try {
        const oldVal = JSON.parse(a.old_value || "{}");
        const newVal = JSON.parse(a.new_value || "{}");
      
        // ✅ 1. START DATE (PRIORITY: TASK_STARTED)
        if (a.action === "TASK_STARTED") {
          if (!task.startDate || new Date(a.created_at) < new Date(task.startDate)) {
            task.startDate = a.created_at;
          }
        }
      
        // ✅ 2. FALLBACK (if no TASK_STARTED, then use old_value TODO)
        else if (oldVal.status === "TODO") {
          if (!task.startDate || new Date(a.created_at) < new Date(task.startDate)) {
            task.startDate = a.created_at;
          }
        }
      
        // ✅ 3. COMPLETED DATE
        if (newVal.status === "DONE") {
          task.completedDate = a.created_at;
        }
      
      } catch (e) {}

    //   try {
    //     const oldVal = JSON.parse(a.old_value || "{}");
    //     const newVal = JSON.parse(a.new_value || "{}");
      
    //     // ✅ START DATE → ONLY from old_value
    //     if (oldVal.status === "TODO") {
    //       if (!task.startDate || new Date(a.created_at) < new Date(task.startDate)) {
    //         task.startDate = a.created_at;
    //       }
    //     }
      
    //     // ✅ COMPLETED DATE
    //     if (newVal.status === "DONE") {
    //       task.completedDate = a.created_at;
    //     }
      
    //   } catch (e) {}
  
    //   // 👉 COMPLETED DATE (when status becomes DONE)
      try {
        const newVal = JSON.parse(a.new_value || "{}");
  
        if (newVal.status === "DONE") {
          task.completedDate = a.created_at;
        }
      } catch (e) {}
    });
  
    return Object.values(groups).sort(
      (a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate)
    );
  }, [allActivities]);
 
  const selectedTask = useMemo(() =>
    taskGroups.find(t => t.id === selectedTaskId),
    [taskGroups, selectedTaskId]
  );
 
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4 bg-white/5 rounded-3xl backdrop-blur-xl border border-white/10">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
        </div>
        <span className="text-sm text-purple-300 font-medium tracking-widest uppercase animate-pulse">Syncing Journey</span>
      </div>
    );
  }
 
  if (!taskGroups.length) {
    return (
      <div className="flex flex-col items-center justify-center p-16 bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl text-center">
        <Sparkles className="w-10 h-10 text-gray-400 opacity-30 mb-6" />
        <h3 className="text-white font-bold text-xl mb-2">No Task Journey Yet</h3>
        <p className="text-sm text-white/40 max-w-[260px]">Every great achievement starts with a single step. Start a task to begin your journey!</p>
      </div>
    );
  }
 
  return (
    <div className="relative space-y-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 px-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="w-4 h-4 text-purple-400" />
            <span className="text-[10px] font-bold text-purple-400 uppercase tracking-[0.2em]">Activity Hub</span>
          </div>
          <h3 className="text-2xl font-white text-white tracking-tight">Active Journeys</h3>
        </div>
        <div className="px-4 py-2 bg-white/5 rounded-2xl border border-white/10 text-white font-white">
   
          {taskGroups.length} <span className="text-white/40 font-bold uppercase text-[10px] tracking-wider ml-1">Total Tasks</span>
        </div>
{/* 
        <button
  onClick={() => {
    setShowAll(true);
    setSelectedTaskId(null);
  }}
  className="px-4 py-2 bg-white/5 rounded-2xl border border-white/10 text-white font-white hover:bg-white/10 transition"
>
  {taskGroups.length}
  <span className="text-white/40 font-bold uppercase text-[10px] tracking-wider ml-1">
    Total Tasks
  </span>
</button> */}
      </div>
 
      <div className="relative">
        <div   
        ref={scrollRef} 
        className="flex gap-5 overflow-x-auto pb-6 pt-2 px-2 no-scrollbar scroll-smooth">
          {taskGroups.map((task, i) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              className={`
                flex-shrink-0 w-[280px] p-6 rounded-[2rem] border transition-all duration-500 cursor-pointer relative
                ${selectedTaskId === task.id
                  ? "bg-gradient-to-br from-purple-600 to-indigo-700 border-white/30 shadow-2xl shadow-purple-900/40"
                  : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"}
              `}
              onClick={() => setSelectedTaskId(task.id)}
            >
              {/* Status Indicator */}
              {task.status === "DONE" && (
                <div className="absolute top-4 right-4 text-emerald-400 animate-in zoom-in duration-300">
                  <CheckCircle2 className="w-6 h-6 fill-emerald-400/10" />
                </div>
              )}
 
 
 
              {/* Assigned Badge */}
              {task.created_by && String(task.created_by) !== String(userId) && (
                <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[9px] font-bold px-3 py-1 rounded-full shadow-xl border border-white/20 z-20 whitespace-nowrap uppercase tracking-widest">
                  Assigned
                </div>
              )}
 
              <div className="flex justify-between items-start mb-10">
                <div className={`p-3 rounded-2xl ${selectedTaskId === task.id ? "bg-white/20" : "bg-purple-500/10"}`}>
                  <Layout className={`w-6 h-6 ${selectedTaskId === task.id ? "text-white" : "text-purple-400"}`} />
                </div>
                {task.dueDate && (

                  <div className={`flex items-center  gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase transition-colors
                    ${new Date(task.dueDate) < new Date().setHours(0, 0, 0, 0) && task.status !== 'DONE'
                      ? task.status === 'HOLD'
                        ? "bg-orange-500/20 text-orange-400 border border-orange-500/30"
                        : "bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse"
                      : selectedTaskId === task.id ? "bg-white/20 text-white" : "bg-white/5 text-amber-300 border border-amber-300/20"}
                  `}>
                    {new Date(task.dueDate) < new Date().setHours(0, 0, 0, 0) && task.status !== 'DONE' ? (
                      task.status === 'HOLD' ? <Clock className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />
                    ) : (
                      <Calendar className="w-3 h-3" />
                    )}
                    {formatSimpleDate(task.dueDate)}
                    {new Date(task.dueDate) < new Date().setHours(0, 0, 0, 0) && task.status !== 'DONE'
                      ? task.status === 'HOLD' ? " • ON HOLD" : " • OVERDUE"
                      : ""}
                  </div>
                )}
              </div>
 
              <h4 className={`text-lg font-bold mb-2 line-clamp-1 ${selectedTaskId === task.id ? "text-white" : "text-white/90"}`}>
                {task.title}
              </h4>
 
              <div className="flex items-center gap-2 mb-6">
                <span className={`text-[10px] font-bold ${selectedTaskId === task.id ? "text-white/60" : "text-white/30"}`}>
                  {task.activities.length} updates • Last on {formatSimpleDate(task.lastUpdate)}
                </span>
              </div>
 
              <button
                className={`
                  w-full py-3 rounded-2xl flex items-center justify-center gap-2 text-[11px] font-white uppercase tracking-widest transition-all
                  ${selectedTaskId === task.id
                    ? "bg-white text-purple-700 shadow-lg"
                    : "bg-white/5 text-white/80 hover:bg-white/10"}
                `}
              >
                {selectedTaskId === task.id ? "Viewing Now" : (task.status === 'DONE' ? "Review History" : "View Journey")}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          ))}
        </div>
        <div className="absolute top-0 right-0 bottom-6 w-20 bg-gradient-to-l from-[#12082f] to-transparent pointer-events-none" />
      </div>
 
      <AnimatePresence mode="wait">
        {selectedTaskId ? (
          <motion.div
            key={selectedTaskId}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 rounded-[3rem] p-8 md:p-12 border border-white/10 relative"
          >
            {/* <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10">
                <History className="w-6 h-6 text-purple-400" />
              </div>
              <div>

              <div>
                <h5 className="text-xl font-bold text-white">Task Timeline</h5>
                <p className="text-xs text-white/40 font-medium">Progress log for <span className="text-purple-400">"{selectedTask?.title}"</span></p>
                </div>

  
              </div>
              {selectedTask?.status === 'DONE' && (
                <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/20 rounded-2xl text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-[10px] font-white uppercase tracking-widest">Completed</span>
                </div>
              )}
            </div> */}


<div className="flex items-center gap-4 mb-12">
  <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10">
    <History className="w-6 h-6 text-purple-400" />
  </div>

  <div>
    <h5 className="text-xl font-bold text-white">Task Timeline</h5>
    <p className="text-xs text-white/40 font-medium">
      Progress log for <span className="text-purple-400">"{selectedTask?.title}"</span>
    </p>
  </div>

  {/* 👉 RIGHT SIDE DATES */}
  <div className="ml-auto flex items-center gap-2 flex-wrap">

{/* START DATE */}
{selectedTask?.startDate && (
  <div className="flex items-center gap-1 px-4 py-2 rounded-full text-[12px] bg-blue-500/10 text-blue-300 border border-blue-400/20">
    <Clock className="w-3 h-3" />
    Start: {formatSimpleDate(selectedTask.startDate)}
  </div>
)}

{/* END DATE (DUE DATE) */}
{selectedTask?.dueDate && (
  <div className="flex items-center gap-1 px-4 py-2 rounded-full text-[12px] bg-amber-500/10 text-amber-300 border border-amber-400/20">
    <Calendar className="w-3 h-3" />
    Due Date: {formatSimpleDate(selectedTask.dueDate)}
  </div>
)}

{/* EXTENDED / COMPLETED DATE */}
{selectedTask?.completedDate && (
  <div className="flex items-center gap-1 px-4 py-2 rounded-full text-[12px] bg-emerald-500/10 text-emerald-300 border border-emerald-400/20">
    <CheckCircle2 className="w-3 h-3" />
    Completed: {formatSimpleDate(selectedTask.completedDate)}
  </div>
)}

</div>
</div>
 
            <TaskActivity taskId={selectedTaskId} activities={selectedTask?.activities} />
          </motion.div>
        ) : (
          <div className="flex flex-col items-center justify-center p-20 bg-white/5 rounded-[3rem] border-2 border-dashed border-white/5">
            <p className="text-white/20 font-bold uppercase tracking-widest text-[10px]">Select a card to reveal its journey</p>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}





// import React, { useEffect, useState, useMemo } from "react";
// import {
//   History,
//   Layout,
//   ArrowRight,
//   Sparkles,
//   Calendar,
//   Layers,
//   CheckCircle2,
//   AlertCircle
// } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import { getUserActivityApi } from "../../app/api";
// import TaskActivity from "./TaskActivity";
 
// /* ================= HELPERS ================= */
 
// const formatSimpleDate = (date) => {
//   if (!date) return "";
//   const d = new Date(String(date).replace(' ', 'T'));
//   return d.toLocaleDateString("en-IN", {
//     day: 'numeric',
//     month: 'short'
//   });
// };
 
// /* ================= COMPONENT ================= */
 
// export default function UserJourney({ userId }) {
//   const [allActivities, setAllActivities] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedTaskId, setSelectedTaskId] = useState(null);
 
//   useEffect(() => {
//     if (!userId) return;
//     setLoading(true);
//     getUserActivityApi(userId)
//       .then((res) => {
//         setAllActivities(res?.data || []);
//       })
//       .catch(() => setAllActivities([]))
//       .finally(() => setLoading(false));
//   }, [userId]);
 
//   // Group activities by Task and find unique tasks
//   const taskGroups = useMemo(() => {
//     const groups = {};
//     allActivities.forEach(a => {
//       if (!groups[a.task_id]) {
//         groups[a.task_id] = {
//           id: a.task_id,
//           title: a.task_title || "Untitled Task",
//           status: a.task_status,
//           dueDate: a.task_due_date,
//           created_by: a.task_created_by,
//           lastUpdate: a.created_at,
//           activities: []
//         };
//       }
//       groups[a.task_id].activities.push(a);
//       if (new Date(a.created_at) > new Date(groups[a.task_id].lastUpdate)) {
//         groups[a.task_id].lastUpdate = a.created_at;
//       }
//     });
 
//     return Object.values(groups).sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));
//   }, [allActivities]);
 
//   const selectedTask = useMemo(() =>
//     taskGroups.find(t => t.id === selectedTaskId),
//     [taskGroups, selectedTaskId]
//   );
 
//   if (loading) {
//     return (
//       <div className="flex flex-col items-center justify-center p-20 space-y-4 bg-white/5 rounded-3xl backdrop-blur-xl border border-white/10">
//         <div className="relative">
//           <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
//         </div>
//         <span className="text-sm text-purple-300 font-medium tracking-widest uppercase animate-pulse">Syncing Journey</span>
//       </div>
//     );
//   }
 
//   if (!taskGroups.length) {
//     return (
//       <div className="flex flex-col items-center justify-center p-16 bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl text-center">
//         <Sparkles className="w-10 h-10 text-gray-400 opacity-30 mb-6" />
//         <h3 className="text-white font-bold text-xl mb-2">No Task Journey Yet</h3>
//         <p className="text-sm text-white/40 max-w-[260px]">Every great achievement starts with a single step. Start a task to begin your journey!</p>
//       </div>
//     );
//   }
 
//   return (
//     <div className="relative space-y-10">
//       <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 px-2">
//         <div>
//           <div className="flex items-center gap-2 mb-1">
//             <Layers className="w-4 h-4 text-purple-400" />
//             <span className="text-[10px] font-bold text-purple-400 uppercase tracking-[0.2em]">Activity Hub</span>
//           </div>
//           <h3 className="text-2xl font-white text-white tracking-tight">Active Journeys</h3>
//         </div>
//         <div className="px-4 py-2 bg-white/5 rounded-2xl border border-white/10 text-white font-white">
//           {taskGroups.length} <span className="text-white/40 font-bold uppercase text-[10px] tracking-wider ml-1">Total Tasks</span>
//         </div>
//       </div>
 
//       <div className="relative">
//         <div className="flex gap-5 overflow-x-auto pb-6 pt-2 px-2 no-scrollbar scroll-smooth">
//           {taskGroups.map((task, i) => (
//             <motion.div
//               key={task.id}
//               initial={{ opacity: 0, scale: 0.9 }}
//               animate={{ opacity: 1, scale: 1 }}
//               transition={{ delay: i * 0.05 }}
//               className={`
//                 flex-shrink-0 w-[280px] p-6 rounded-[2rem] border transition-all duration-500 cursor-pointer relative
//                 ${selectedTaskId === task.id
//                   ? "bg-gradient-to-br from-purple-600 to-indigo-700 border-white/30 shadow-2xl shadow-purple-900/40"
//                   : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"}
//               `}
//               onClick={() => setSelectedTaskId(task.id)}
//             >
//               {/* Status Indicator */}
//               {task.status === "DONE" && (
//                 <div className="absolute top-4 right-4 text-emerald-400 animate-in zoom-in duration-300">
//                   <CheckCircle2 className="w-6 h-6 fill-emerald-400/10" />
//                 </div>
//               )}



//                   {/* Assigned Badge */}
//                   {task.created_by && String(task.created_by) !== String(userId) && (
//                 <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[9px] font-bold px-3 py-1 rounded-full shadow-xl border border-white/20 z-20 whitespace-nowrap uppercase tracking-widest">
//                   Assigned
//                 </div>
//               )}
 
//               <div className="flex justify-between items-start mb-10">
//                 <div className={`p-3 rounded-2xl ${selectedTaskId === task.id ? "bg-white/20" : "bg-purple-500/10"}`}>
//                   <Layout className={`w-6 h-6 ${selectedTaskId === task.id ? "text-white" : "text-purple-400"}`} />
//                 </div>
//                 {task.dueDate && (
//                   // <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-white uppercase ${selectedTaskId === task.id ? "bg-white/20 text-white" : "bg-white/5 text-amber-300 border border-amber-300/20"}`}>
//                   //   <Calendar className="w-3 h-3" />
//                   //   {formatSimpleDate(task.dueDate)}


//                   <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase transition-colors
//                     ${new Date(task.dueDate) < new Date().setHours(0,0,0,0) && task.status !== 'DONE'
//                       ? "bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse"
//                       : selectedTaskId === task.id ? "bg-white/20 text-white" : "bg-white/5 text-amber-300 border border-amber-300/20"}
//                   `}>
//                     {new Date(task.dueDate) < new Date().setHours(0,0,0,0) && task.status !== 'DONE' ? (
//                       <AlertCircle className="w-3 h-3" />
//                     ) : (
//                       <Calendar className="w-3 h-3" />
//                     )}
//                     {formatSimpleDate(task.dueDate)}
//                     {new Date(task.dueDate) < new Date().setHours(0,0,0,0) && task.status !== 'DONE' ? " • OVERDUE" : ""}
//                   </div>
//                 )}
//               </div>
 
//               <h4 className={`text-lg font-bold mb-2 line-clamp-1 ${selectedTaskId === task.id ? "text-white" : "text-white/90"}`}>
//                 {task.title}
//               </h4>
 
//               <div className="flex items-center gap-2 mb-6">
//                 <span className={`text-[10px] font-bold ${selectedTaskId === task.id ? "text-white/60" : "text-white/30"}`}>
//                   {task.activities.length} updates • Last on {formatSimpleDate(task.lastUpdate)}
//                 </span>
//               </div>
 
//               <button
//                 className={`
//                   w-full py-3 rounded-2xl flex items-center justify-center gap-2 text-[11px] font-white uppercase tracking-widest transition-all
//                   ${selectedTaskId === task.id
//                     ? "bg-white text-purple-700 shadow-lg"
//                     : "bg-white/5 text-white/80 hover:bg-white/10"}
//                 `}
//               >
//                 {selectedTaskId === task.id ? "Viewing Now" : (task.status === 'DONE' ? "Review History" : "View Journey")}
//                 <ArrowRight className="w-3.5 h-3.5" />
//               </button>
//             </motion.div>
//           ))}
//         </div>
//         <div className="absolute top-0 right-0 bottom-6 w-20 bg-gradient-to-l from-[#12082f] to-transparent pointer-events-none" />
//       </div>
 
//       <AnimatePresence mode="wait">
//         {selectedTaskId ? (
//           <motion.div
//             key={selectedTaskId}
//             initial={{ opacity: 0, y: 30 }}
//             animate={{ opacity: 1, y: 0 }}
//             className="bg-white/5 rounded-[3rem] p-8 md:p-12 border border-white/10 relative"
//           >
//             <div className="flex items-center gap-4 mb-12">
//               <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10">
//                 <History className="w-6 h-6 text-purple-400" />
//               </div>
//               <div>
//                 <h5 className="text-xl font-bold text-white">Task Timeline</h5>
//                 <p className="text-xs text-white/40 font-medium">Progress log for <span className="text-purple-400">"{selectedTask?.title}"</span></p>
//               </div>
//               {selectedTask?.status === 'DONE' && (
//                 <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/20 rounded-2xl text-emerald-400">
//                   <CheckCircle2 className="w-4 h-4" />
//                   <span className="text-[10px] font-white uppercase tracking-widest">Completed</span>
//                 </div>
//               )}
//             </div>
 
//             <TaskActivity taskId={selectedTaskId} activities={selectedTask?.activities} />
//           </motion.div>
//         ) : (
//           <div className="flex flex-col items-center justify-center p-20 bg-white/5 rounded-[3rem] border-2 border-dashed border-white/5">
//             <p className="text-white/20 font-bold uppercase tracking-widest text-[10px]">Select a card to reveal its journey</p>
//           </div>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// }